import Add16 from '@carbon/icons-react/es/add/16';
import { isEmpty, set } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { JiraConfig, JiraField } from 'api/integrations';
import { TaskId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { MiniTable } from 'components/molecules/table';
import { toastService } from 'components/molecules/toast';
import { Caption01 } from 'components/styles';
import { ConversationContext } from 'data/conversation';
import { useTranslation } from 'hooks/use-translation';
import {
  useGetJiraIssue,
  useJiraIssueDeleteMutation,
  useJiraIssueUnlinkMutation,
} from 'queries/jira-integration';
import { getTaskDetailQueryKey } from 'queries/task';
import { getColumns } from './columns';
import { CommentBox } from './comment-box';

const Table = styled(MiniTable)`
  .ant-table {
    background: var(--private-02);
    margin-bottom: var(--spacing-05);
  }
  .ant-table-thead > tr > th {
    ${Caption01};
    color: var(--gray-80);
  }

  .ant-table-row:hover {
    background: transparent;
  }
`;

type JiraIssue = {
  context: ConversationContext;
  jiraIssueId: string;
  jiraConfig: JiraConfig;
  resourceMappingId: number;
  projectId: number;
};

const reducer = (acc, field: JiraField) =>
  set(acc, `${field.integrationFieldId}`, field);

export const JiraIssueDetails: FC<JiraIssue> = ({
  jiraConfig,
  jiraIssueId,
  projectId,
  resourceMappingId,
  context,
}) => {
  const saveText = useTranslation('yes');
  const successTitle = useTranslation('success');
  const errorTitle = useTranslation('failure');
  const deleteSuccessMessage = useTranslation(
    'task.delete_jira_issue.success.message',
  );
  const deleteErrorMessage = useTranslation(
    'task.delete_jira_issue.error.message',
  );
  const unlinkSuccessMessage = useTranslation(
    'task.unlink_jira_issue.success.message',
  );
  const unlinkErrorMessage = useTranslation(
    'task.unlink_jira_issue.error.message',
  );
  const addCommentText = useTranslation('task.add_jira_comment');
  const deleteModalTitle = useTranslation('task.delete_jira_issue.title');
  const deleteModalMessage = useTranslation('task.delete_jira_issue.message');

  const queryClient = useQueryClient();
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const hasSyncEnabled = useMemo(
    () => jiraConfig.settingsMeta.syncTaskComments,
    [jiraConfig],
  );

  const { mutate: jiraIssueDeleteMutation, isLoading: deletingIssue } =
    useJiraIssueDeleteMutation(jiraConfig.integrationId);
  const { mutate: jiraIssueUnlinkMutation, isLoading: unlinkingIssue } =
    useJiraIssueUnlinkMutation(jiraConfig.integrationId);
  const { data: jiraIssue, isLoading } = useGetJiraIssue(
    jiraConfig.integrationId,
    jiraIssueId,
  );

  const onJiraIssueDelete = () => {
    jiraIssueDeleteMutation(jiraIssueId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTaskDetailQueryKey(TaskId(projectId, context.id)),
        );
        toastService({
          title: successTitle,
          message: deleteSuccessMessage,
        }).success();
        setShowDeleteConfirm(false);
      },
      onError: () => {
        toastService({
          title: errorTitle,
          message: deleteErrorMessage,
        }).error();
      },
    });
  };

  const onJiraIssueUnlink = () => {
    jiraIssueUnlinkMutation(resourceMappingId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTaskDetailQueryKey(TaskId(projectId, context.id)),
        );
        toastService({
          title: successTitle,
          message: unlinkSuccessMessage,
        }).success();
      },
      onError: () => {
        toastService({
          title: errorTitle,
          message: unlinkErrorMessage,
        }).error();
      },
    });
  };

  const columns = getColumns({
    issueUrl: jiraIssue?.url,
    handleDelete: () => setShowDeleteConfirm(true),
    handleUnlink: onJiraIssueUnlink,
  });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      {!isEmpty(jiraIssue?.fields) && (
        <Table
          size="small"
          columns={columns}
          dataSource={[jiraIssue?.fields.reduce(reducer, {})]}
          pagination={false}
        />
      )}
      {!showCommentBox && hasSyncEnabled && (
        <Button
          type="link"
          size="small"
          icon={<Add16 />}
          onClick={() => setShowCommentBox(true)}
          disabled={deletingIssue || unlinkingIssue}
        >
          {addCommentText}
        </Button>
      )}
      {showCommentBox && hasSyncEnabled && (
        <CommentBox
          onClose={() => setShowCommentBox(false)}
          context={context}
        />
      )}
      <Modal
        visible={showDeleteConfirm}
        title={deleteModalTitle}
        confirmLoading={false}
        onCancel={() => setShowDeleteConfirm(false)}
        okButtonProps={{
          danger: true,
          loading: deletingIssue,
        }}
        onOk={onJiraIssueDelete}
        okText={saveText}
      >
        {deleteModalMessage}
      </Modal>
    </>
  );
};
